import { useCallback, useRef, useState } from "react";
import { Button } from "@mui/material";

import "./style.scss";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import HeaderLayout from "../../../components/layout/header-layout.comp";
import { LoaderView } from "../../../components/loader/loader.comp";
import { ToastContainer } from "../../../components/toast/toast.comp";
import TenantAuthentication, { TenantAuthenticationHandle } from "./tenant-authentication.comp";

/**
 * /authentication — the tenant's own Authentication settings, for admins
 * holding the per-user `authSettings` permission
 * (tenant_auth_delegation_design.md §4.1). Same component as the super
 * admin's Tenant Properties tab, scoped to the signed-in user's company; the
 * AuthSettingsAdmin guard behind every call decides who may.
 */
const TenantAuthenticationView = () => {
    const { user } = FirebaseAuthContainer.useContainer();
    const { showToast } = ToastContainer.useContainer();
    const authRef = useRef<TenantAuthenticationHandle>(null);
    const [dirty, setDirty] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    // Bumped to remount the form and reload the saved settings.
    const [formKey, setFormKey] = useState<number>(0);

    const onDirtyChange = useCallback((value: boolean) => setDirty(value), []);

    const handleSave = async () => {
        if (!authRef.current) return;
        setIsSaving(true);
        try {
            if (await authRef.current.save()) {
                showToast("Authentication settings saved", 3000);
            }
        } finally {
            setIsSaving(false);
        }
    };

    if (!user) return <LoaderView show={true} />;

    return (
        <HeaderLayout blurHeader={false}>
            <div className="tenant-auth-page">
                <div className="tenant-auth-page-title">
                    <span className="tenant-auth-page-title__text" role="heading" aria-level={1}>
                        Authentication
                    </span>
                    <div className="tenant-auth-page__actions">
                        <Button
                            variant="text"
                            disabled={!dirty || isSaving}
                            onClick={() => setFormKey(k => k + 1)}
                        >
                            Discard changes
                        </Button>
                        <Button
                            className="tenant-auth-page__save"
                            variant="contained"
                            disabled={!dirty || isSaving}
                            onClick={handleSave}
                        >
                            {isSaving ? "Saving..." : "Save"}
                        </Button>
                    </div>
                </div>
                <div className="tenant-auth-page__body">
                    <TenantAuthentication
                        key={formKey}
                        ref={authRef}
                        companyId={user.companyId}
                        tenantId={user.tenantId}
                        onDirtyChange={onDirtyChange}
                    />
                </div>
            </div>
        </HeaderLayout>
    );
};

export default TenantAuthenticationView;
